// 认证状态(全局 Zustand store)
// 持久化:access + refresh token 存 localStorage,刷新页面后能自动续期
import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { authApi } from '@/api/auth';
import { oauthApi } from '@/api/oauth';
import type { PublicUser } from '@/api/types';

// 侧栏权限过滤(useNavVisibility)所需的派生字段:
//   admin -> ["*"];普通登录用户 -> ["user:view"];未登录 -> null(user 为空)
export type AuthedUser = PublicUser & { permissions: string[] };

function withPermissions(user: PublicUser): AuthedUser {
  const permissions = user.is_admin ? ['*'] : ['user:view'];
  return { ...user, permissions };
}

interface AuthState {
  user: AuthedUser | null;
  accessToken: string | null;
  refreshToken: string | null;
  loading: boolean;
  /** bootstrap 完成标记:启动后首次校验 token 完成后置 true */
  bootstrapped: boolean;
  // 派生
  isAuthenticated: () => boolean;
  // 动作
  login: (username_or_email: string, password: string, captcha_token: string, captcha_x: number) => Promise<void>;
  register: (username: string, email: string, password: string) => Promise<void>;
  /** SSO / OAuth 代理登录:provider 为 settings 里的 provider name */
  oauthLogin: (provider: string, username: string, password: string) => Promise<void>;
  /** SSO / OAuth 标准授权码流程:用一次性 code 兑换 token(provider 回跳后调用) */
  oauthExchangeLogin: (code: string) => Promise<void>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
  /** 直接更新 user 对象(改完 display_name 后用) */
  setUser: (user: PublicUser) => void;
  /** 应用启动时从 localStorage 恢复 + 校验 token 还有效 */
  bootstrap: () => Promise<void>;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      accessToken: null,
      refreshToken: null,
      loading: false,
      bootstrapped: false,

      isAuthenticated: () => !!get().accessToken && !!get().user,

      async login(username_or_email, password, captcha_token, captcha_x) {
        set({ loading: true });
        try {
          const { user, access_token, refresh_token } = await authApi.login({
            username_or_email,
            password,
            captcha_token,
            captcha_x,
          });
          set({ user: withPermissions(user), accessToken: access_token, refreshToken: refresh_token, loading: false });
        } catch (e) {
          set({ loading: false });
          throw e;
        }
      },

      async register(username, email, password) {
        set({ loading: true });
        try {
          const { user, access_token, refresh_token } = await authApi.register({
            username,
            email,
            password,
          });
          set({ user: withPermissions(user), accessToken: access_token, refreshToken: refresh_token, loading: false });
        } catch (e) {
          set({ loading: false });
          throw e;
        }
      },

      async oauthLogin(provider, username, password) {
        set({ loading: true });
        try {
          const { user, access_token, refresh_token } = await oauthApi.login(
            provider,
            username,
            password,
          );
          set({ user: withPermissions(user), accessToken: access_token, refreshToken: refresh_token, loading: false });
        } catch (e) {
          set({ loading: false });
          throw e;
        }
      },

      async oauthExchangeLogin(code) {
        set({ loading: true });
        try {
          const { user, access_token, refresh_token } = await oauthApi.exchange(code);
          set({ user: withPermissions(user), accessToken: access_token, refreshToken: refresh_token, loading: false });
        } catch (e) {
          set({ loading: false });
          throw e;
        }
      },

      async logout() {
        // 后端暂无 /v1/auth/logout 端点(只清 cookie / 黑名单 refresh token),
        // Phase 4 只清前端状态
        set({ user: null, accessToken: null, refreshToken: null });
      },

      async refresh() {
        const refreshToken = get().refreshToken;
        if (!refreshToken) throw new Error('no refresh token');
        const { user, access_token } = await authApi.refresh(refreshToken);
        set({ user: withPermissions(user), accessToken: access_token });
      },

      setUser(user) {
        set({ user: withPermissions(user) });
      },

      async bootstrap() {
        const { accessToken, refreshToken } = get();
        console.log(
          '[gitman-dbg] bootstrap enter: accessToken?',
          !!accessToken,
          '| refreshToken?',
          !!refreshToken,
        );
        if (!accessToken || !refreshToken) {
          set({ bootstrapped: true });
          console.log('[gitman-dbg] bootstrap: no tokens -> bootstrapped=true, stop waiting');
          return;
        }
        try {
          // 用 access token 拉 /me 校验
          const { user } = await authApi.me();
          set({ user: withPermissions(user), bootstrapped: true });
          console.log('[gitman-dbg] bootstrap: /me OK, user=', user?.username, '-> bootstrapped=true');
        } catch (e) {
          console.warn('[gitman-dbg] bootstrap: /me failed, trying refresh:', (e as Error)?.message, e);
          // access 过期,用 refresh 换
          try {
            const { user, access_token } = await authApi.refresh(refreshToken);
            set({ user: withPermissions(user), accessToken: access_token, bootstrapped: true });
            console.log('[gitman-dbg] bootstrap: refresh OK, user=', user?.username, '-> bootstrapped=true');
          } catch (e2) {
            // 全过期,清状态
            set({ user: null, accessToken: null, refreshToken: null, bootstrapped: true });
            console.warn('[gitman-dbg] bootstrap: refresh failed too, cleared state -> bootstrapped=true', (e2 as Error)?.message, e2);
          }
        }
      },
    }),
    {
      name: 'gitman-auth',
      storage: createJSONStorage(() => localStorage),
      // 只持久化 token,user 每次 bootstrap 时从 /me 重新拉
      partialize: (state) => ({
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
      }),
      // bootstrapped 不持久化,每次刷新都从 false 开始,等 bootstrap() 完成后才 true
      skipHydration: false,
    },
  ),
);
